import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { makeStyles } from '@material-ui/core';
import MastercodeUnlockAnnouncement from 'components/@common/Announcements/MastercodeUnlockAnnouncement';
import UserClassesInformation from 'components/@common/UserProfile/UserClassesInformation';
import PageContainer from 'components/@layout/PageContainer';
import EndOfYearProcessingPanel from 'components/EndOfYearProcessing/components/EndOfYearProcessingPanel';
import { EndOfYearProcessingNotificationType } from 'components/EndOfYearProcessing/types';
import GET_MY_ORGANIZATION_NAMES_CLASSROOMS_AND_MASTER_CODES from 'graph/composite/get-my-organization-names-classrooms-and-master-codes-query';
import useUserStore from 'store/user';
import { ModuleProps } from 'types';
import { UserOrganization } from 'types/schema-generated-types';
import { proxyInsufficientPrivilegesError } from 'utilities/proxy-apollo-error';
import HeaderBar from '../HeaderBar';
import TeacherApplicationInformation from './components/TeacherApplicationInformation';
import UserInformation from './components/UserInformation';

const Dashboard: React.FC<ModuleProps> = props => {
  const classes = useStyles();
  const { t } = useTranslation('Dashboard');
  const [{ me }] = useUserStore();

  const { data, loading, error } = useQuery(GET_MY_ORGANIZATION_NAMES_CLASSROOMS_AND_MASTER_CODES, {
    variables: { userId: me?.id },
    errorPolicy: 'all'
  });

  const getOrganizations = React.useCallback((): string => {
    return data?.user.organizationAssociations
      .map(({ organization }: UserOrganization) => organization.name)
      .join(', ');
  }, [data]);

  const organizations = getOrganizations();
  const classrooms = data?.classrooms;
  const classroomsCount = data?.classrooms?.length || 0;

  return (
    <PageContainer loading={loading} error={proxyInsufficientPrivilegesError(error)}>
      <HeaderBar {...props} />
      <div className={classes.container}>
        {me && data?.user && (
          <UserInformation
            firstName={me?.firstName}
            lastName={me?.lastName}
            avatarUrl={me?.avatarUrl}
            username={me?.username}
            pin={me?.pin}
            type={data?.user.type}
            organizations={organizations}
          />
        )}
        <EndOfYearProcessingPanel
          hideTypes={[EndOfYearProcessingNotificationType.NEW_YEAR_IMPLEMENTED]}
        />
        <MastercodeUnlockAnnouncement href="/dashboard/orders" mastercodes={data?.mastercodes} />
        <div>
          <TeacherApplicationInformation />
          {data?.classrooms && classroomsCount > 0 && (
            <div className={classes.userClassesContainer}>
              <UserClassesInformation
                classrooms={classrooms}
                emptyMessage={t('You have no active classes.')}
                btnText={'View My Classes & Students'}
              />
            </div>
          )}
        </div>
      </div>
    </PageContainer>
  );
};

const useStyles = makeStyles(theme => ({
  container: {
    padding: `${theme.spacing(3)}px ${theme.spacing(5)}px`,
    display: 'flex',
    flexDirection: 'column',
    gap: `${theme.spacing(4)}px`
  },
  userClassesContainer: {
    gridArea: '3 / 1 / 4 / 4',
    marginTop: theme.spacing(5)
  }
}));

export default Dashboard;
